import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@mui/material";
import { handleAxiosError } from "app/services/app.error.handler";
import { loadAllUsersFromBackend, loadUsersPageFromBackend, deleteUser } from "app/services/user.service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { useDispatch, useSelector } from "react-redux";
import { userPageActions } from "store/user-page-slice";
import { userActions } from "store/user-slice";
import { UsersDataGrid } from "./UsersDataGrid";
import AddUserDialog from "./AddUserDialog";
import { ConfirmDialog } from "../ConfirmDialog";

const AllUsersView = () => {
  const dispatch = useDispatch();
  const { rows, totalElements, page, pageSize, sortBy, sortDirection, searchQuery, loading } =
    useSelector((state) => state.userPage);

  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  // Guards against out-of-order responses when the user rapidly changes page/size/sort:
  // only the most recently dispatched fetch is allowed to update the slice.
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(
    (overrides = {}) => {
      const params = {
        page: overrides.page ?? page,
        pageSize: overrides.pageSize ?? pageSize,
        sortBy: overrides.sortBy ?? sortBy,
        sortDirection: overrides.sortDirection ?? sortDirection,
        searchQuery: overrides.searchQuery ?? searchQuery,
      };
      const seq = ++requestSeqRef.current;
      dispatch(userPageActions.setLoading(true));
      loadUsersPageFromBackend(params)
        .then((response) => {
          if (seq !== requestSeqRef.current) return;
          const body = response.data || {};
          dispatch(
            userPageActions.replacePage({
              rows: Array.isArray(body.content) ? body.content : [],
              totalElements: typeof body.totalElements === "number" ? body.totalElements : 0,
              page: typeof body.page === "number" ? body.page : params.page,
              pageSize: typeof body.pageSize === "number" ? body.pageSize : params.pageSize,
            })
          );
        })
        .catch((error) => {
          if (seq !== requestSeqRef.current) return;
          dispatch(
            userPageActions.replacePage({
              rows: [],
              totalElements: 0,
              page: params.page,
              pageSize: params.pageSize,
            })
          );
          handleAxiosError(error);
        });
    },
    [dispatch, page, pageSize, sortBy, sortDirection, searchQuery]
  );

  // Keeps the global user list (state.user.rows) in sync after a mutation.
  // Picker dropdowns like the Client's Manager Mappings user dropdown read
  // from this list, which is otherwise only loaded once on page mount.
  const refreshPickerUsers = useCallback(() => {
    loadAllUsersFromBackend()
      .then((response) =>
        dispatch(userActions.replaceData(Array.isArray(response.data) ? response.data : []))
      )
      .catch((error) => handleAxiosError(error));
  }, [dispatch]);

  const refreshAfterMutation = useCallback(() => {
    fetchPage();
    refreshPickerUsers();
  }, [fetchPage, refreshPickerUsers]);

  useEffect(() => {
    fetchPage();
    // Reset the cached search when the user leaves this screen so the next
    // visit starts with an empty search box rather than the last-typed query.
    return () => {
      dispatch(userPageActions.setSearchQuery(''));
    };
    // Only fire on mount; subsequent fetches go through handlers below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaginationModelChange = (model) => {
    const nextPage = model.page;
    const nextPageSize = model.pageSize;
    if (nextPage === page && nextPageSize === pageSize) return;
    dispatch(userPageActions.setPagination({ page: nextPage, pageSize: nextPageSize }));
    fetchPage({ page: nextPage, pageSize: nextPageSize });
  };

  const handleSortModelChange = ({ sortBy: nextSortBy, sortDirection: nextSortDirection }) => {
    if (nextSortBy === sortBy && nextSortDirection === sortDirection) return;
    dispatch(userPageActions.setSort({ sortBy: nextSortBy, sortDirection: nextSortDirection }));
    fetchPage({ page: 0, sortBy: nextSortBy, sortDirection: nextSortDirection });
  };

  const handleSearchChange = (nextQuery) => {
    const normalized = (nextQuery ?? '').trim();
    if (normalized === (searchQuery ?? '')) return;
    dispatch(userPageActions.setSearchQuery(normalized));
    fetchPage({ page: 0, searchQuery: normalized });
  };

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(`Are you sure you want to delete this User (Name: '${data.fullName}')?`);
    setDeleteId(data.id);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteUser(deleteId)
      .then(() => refreshAfterMutation())
      .catch((error) => handleAxiosError(error));
  };

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            All Users
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <UsersDataGrid
            users={rows}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDirection={sortDirection}
            searchQuery={searchQuery}
            isLoading={loading}
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            onSearchChange={handleSearchChange}
            onAddUser={() => {
              setEditObject(null);
              setOpen(true);
            }}
            editEventHandler={editEventHandler}
            confirmDeleteDialogHandler={confirmDeleteDialogHandler}
          />
        </MDBox>
      </Card>
      <AddUserDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={refreshAfterMutation}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This User?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </div>
  );
};

export default AllUsersView;
